<script setup lang="ts">
import { computed, ref, type Component } from 'vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaMenuSurface from '@/components/design/base/GaiaMenuSurface.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import { IconCheck, IconChevronDown } from '@/components/design/icons';
import { gaiaNeutral } from '@/components/design/themes/tokens';
import { useSigningProviderOptions } from '@/composables/useSigningProviderOptions';

/**
 * The compose step's primary action, shaped like GitHub's merge button: the
 * button runs the chosen way of finishing, the caret picks the other. Sending
 * for signing always opens the signing dialog (provider, signature type,
 * order, message, expiry); saving files the document in one click.
 */
type FinalizeMode = 'sign' | 'save';

const props = defineProps({
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  /**
   * What still stops sending for signing, one line per signing slot; empty
   * when ready. Null when the template places no signatures.
   */
  signerGaps: { type: Array as () => string[] | null, default: null },
  /** Where the document lands, e.g. "My Vault / Agreements". */
  folderLabel: { type: String, default: 'My Vault' },
  /** Extra attributes for the main button (the tour's data-tour key). */
  mainAttrs: { type: Object as () => Record<string, string>, default: undefined },
});

const emit = defineEmits<{ finalize: [mode: FinalizeMode] }>();

const { providerOptions } = useSigningProviderOptions();

/** The template signs and the organisation has something to sign with. */
const canSign = computed(() => props.signerGaps !== null && providerOptions.value.length > 0);
const signReady = computed(() => canSign.value && props.signerGaps?.length === 0);

/**
 * What the author picked from the caret; until then, signing whenever it is on
 * offer. A signer still missing details doesn't flip the button to "Save":
 * it stays "send for signing", disabled, and says what's missing — saving
 * without sending is a deliberate pick from the caret.
 */
const picked = ref<FinalizeMode | null>(null);
const mode = computed<FinalizeMode>(() => {
  const wanted = picked.value ?? 'sign';
  return wanted === 'sign' && canSign.value ? 'sign' : 'save';
});
const mainDisabled = computed(() => props.disabled || (mode.value === 'sign' && !signReady.value));

/** Why it can't send yet, one line per signer — the tooltip on the buttons. */
const hints = computed<string[]>(() => {
  if (props.disabled || mode.value !== 'sign' || signReady.value) return [];
  const gaps = props.signerGaps ?? [];
  return gaps.length > 0 ? gaps : ['The signers are not ready yet.'];
});

interface MenuRow {
  key: FinalizeMode;
  title: string;
  description: string;
  /** The tick for the mode in effect. */
  icon: Component | null;
}

const menuOpen = ref(false);

const rows = computed<MenuRow[]>(() => [
  ...(canSign.value
    ? [
        {
          key: 'sign' as const,
          title: 'Save & sign',
          // Pickable while signers are incomplete: the button then says what's missing.
          description: signReady.value
            ? 'Saves the contract and sends it to the signers. You set the signing details first.'
            : (props.signerGaps?.[0] ?? 'The signers are not ready yet.'),
          icon: mode.value === 'sign' ? IconCheck : null,
        },
      ]
    : []),
  {
    key: 'save',
    title: 'Save document',
    description: `Saves the contract to ${props.folderLabel}. Nothing is sent.`,
    icon: mode.value === 'save' ? IconCheck : null,
  },
]);

function onSelect(row: MenuRow): void {
  menuOpen.value = false;
  picked.value = row.key;
}

/**
 * Inline custom properties rather than `v-bind()`: the popover teleports to
 * <body>, where the vars `v-bind()` writes on this component's root never reach.
 */
const styleVars = {
  '--finalize-hover': gaiaNeutral.surfaceHover,
};
</script>

<template>
  <!-- A disabled <button> fires no mouse events, so the span is the hover
       target (same as GaiaWizardFooter's next tooltip). -->
  <gaia-tooltip placement="top-end" :disabled="hints.length === 0 || menuOpen">
    <template #trigger>
      <span style="display: inline-flex">
        <n-button-group>
          <gaia-button
            v-bind="props.mainAttrs"
            type="primary"
            :loading="props.loading"
            :disabled="mainDisabled"
            @click="emit('finalize', mode)"
          >
            {{ mode === 'sign' ? 'Save & sign' : 'Save document' }}
          </gaia-button>
          <gaia-popover
            v-model:show="menuOpen"
            placement="top-end"
            :disabled="props.disabled || props.loading"
          >
            <template #trigger>
              <gaia-button
                type="primary"
                :disabled="props.disabled || props.loading"
                title="More ways to finish"
                style="padding: 0 10px"
              >
                <n-icon :component="IconChevronDown" :size="16" />
              </gaia-button>
            </template>
            <gaia-menu-surface width="320px" :style="styleVars">
              <button
                v-for="row in rows"
                :key="row.key"
                type="button"
                class="finalize-row"
                @click="onSelect(row)"
              >
                <n-flex :size="10" :wrap="false" align="flex-start">
                  <n-icon
                    :component="row.icon ?? undefined"
                    :size="16"
                    :style="{
                      flex: 'none',
                      marginTop: '2px',
                      visibility: row.icon ? 'visible' : 'hidden',
                    }"
                  />
                  <n-flex vertical :size="2" :style="{ minWidth: 0 }">
                    <gaia-text size="dense" weight="semibold" tone="ink">{{ row.title }}</gaia-text>
                    <gaia-text size="meta" tone="subtle" :style="{ lineHeight: 1.45 }">
                      {{ row.description }}
                    </gaia-text>
                  </n-flex>
                </n-flex>
              </button>
            </gaia-menu-surface>
          </gaia-popover>
        </n-button-group>
      </span>
    </template>
    <n-flex vertical :size="4">
      <gaia-text size="meta" weight="semibold" tone="ink">Before sending for signing</gaia-text>
      <gaia-text v-for="gap in hints" :key="gap" size="meta" tone="text">{{ gap }}</gaia-text>
    </n-flex>
  </gaia-tooltip>
</template>

<style scoped>
/* A menu row with a title and a description: naive's dropdown options have a
   fixed height, so two-line rows are drawn here. Button reset + hover only. */
.finalize-row {
  display: block;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: 6px;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.finalize-row:hover,
.finalize-row:focus-visible {
  background: var(--finalize-hover);
  outline: none;
}
</style>
